import React, { useContext, useEffect, useState } from 'react';
import { Layout, Menu, Typography, notification } from 'antd';
import { QueryClient, QueryClientProvider } from 'react-query';
import DefaultLayout from 'Components/DefaultLayout/DefaultLayout';
import styles from 'Sell/Upsell/Shell/UpsellShell.styles';
import NestedRoutes from 'Sell/Upsell/Shell/NestedRoutes';
import MenuUpsellButton from 'Sell/Upsell/Shell/Components/MenuUpsellButton';
import { ReactComponent as RoomTypeIcon } from 'Sell/assets/icon/room-type-icon.svg';
import { ReactComponent as UpsellStatusIcon } from 'Sell/assets/icon/upsell-status-icon.svg';
import { ReactComponent as ReportsIcon } from 'Sell/assets/icon/reports-icon.svg';
import Routes from 'Sell/Upsell/Router/routes';
import { AppContext } from 'App.context';
import Colors from 'constants/Colors';
import AppButton from 'Components/common/Forms/AppButton/AppButton';
import { ApiContext } from 'utils/networking/Api.context';
import Loading from 'Components/common/Loader/Loading';

const { Sider, Content } = Layout;

const queryClient = new QueryClient();

export default function UpsellShell() {
  const {
    appState,
    appActions: { translate, getSelectedHotel, setHotelLanguages },
  } = useContext(AppContext);

  const { API } = useContext(ApiContext);

  const [state, setState] = useState({
    isLoading: true,
    suscription: null,
    sendSuscriptionRequestEnabled: true,
    hotelLanguages: [],
  });

  const isAdminView = appState.role === 'owner' || appState.role === 'admin';

  useEffect(() => {
    getSuscription();
    loadHotelLanguages(getSelectedHotel()?.key);
  }, [, appState.selectedHotel]);

  const UpsellView = () => {
    return (
      <Layout style={styles.transparentSider}>
        <Sider width={200} style={styles.transparentSider}>
          <Menu mode="vertical" theme="light" style={styles.transparentSider}>
            {isAdminView && (
              <Menu.Item style={styles.customMenuItem} key="1">
                <MenuUpsellButton
                  path={Routes.roomsList}
                  text={translate('upsell.menu.roomType')}
                  IconComponent={RoomTypeIcon}
                />
              </Menu.Item>
            )}
            <Menu.Item style={styles.customMenuItem} key="2">
              <MenuUpsellButton
                path={Routes.upsellsList}
                text={translate('upsell.menu.status')}
                IconComponent={UpsellStatusIcon}
              />
            </Menu.Item>
            <Menu.Item style={styles.customMenuItem} key="3">
              <MenuUpsellButton
                path="/reports"
                text={translate('upsell.menu.report')}
                IconComponent={ReportsIcon}
              />
            </Menu.Item>
          </Menu>
        </Sider>
        <Content>
          <NestedRoutes />
        </Content>
      </Layout>
    );
  };

  const LicenseView = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        width: '100%',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          backgroundColor: Colors.white,
          borderRadius: 16,
          padding: 16,
          maxWidth: 600,
        }}
      >
        <div style={{ fontWeight: 'bold' }}>
          {translate('missingSuscription.title').replace('$1', 'Upselling')}
        </div>
        <div>{translate('missingSuscription.description').replaceAll('$1', 'Upselling')}</div>
        {state.sendSuscriptionRequestEnabled && (
          <div style={{ display: 'flex', justifyContent: 'right', marginTop: 8 }}>
            <AppButton
              text={translate('missingSuscription.send')}
              action={() => requestFunctionality()}
            />
          </div>
        )}
      </div>
    </div>
  );
  return (
    <QueryClientProvider client={queryClient}>
      <DefaultLayout>
        {state.isLoading ? (
          <Loading></Loading>
        ) : state.suscription ? (
          UpsellView({ isAdminView })
        ) : (
          LicenseView
        )}
      </DefaultLayout>
    </QueryClientProvider>
  );

  function getSuscription() {
    API.getAccountFunctionalities({ hotelId: getSelectedHotel()?.key })
      .then(res => {
        const upsellModule = res.find(item => item.key == 'upsell_module');
        updateState({ isLoading: false, suscription: upsellModule.active });
      })
      .catch(err => {
        console.warn(err);
        updateState({ isLoading: false });
      });
  }

  function requestFunctionality() {
    updateState({ isLoading: true });
    API.requestFunctionalityContract({
      hotelId: getSelectedHotel().key,
      suscription: 'upsell_module',
      functionality: 'Upsell Module',
    })
      .then(_ => {
        sendRequestFunctionalityEmail();
      })
      .catch(_ => {
        updateState({ isLoading: false });
      });
  }

  function sendRequestFunctionalityEmail() {
    updateState({ isLoading: true });
    API.sendRequestFuncionalityEmail({
      hotelId: getSelectedHotel().key,
      functionality: 'Upsell Module',
    })
      .then(_ => {
        notification.success({
          message: translate('missingSuscription.requestSent'),
        });
        updateState({ isLoading: false, sendSuscriptionRequestEnabled: false });
      })
      .catch(_ => {
        updateState({ isLoading: false });
      });
  }

  function loadHotelLanguages(hotelId) {
    if (!hotelId) return;
    updateState({ isLoading: true });
    API.appearenceRequest(hotelId)
      .then(res => {
        const mapIdToCode = id => {
          const map = {
            1: 'en',
            2: 'es',
            3: 'de',
            4: 'ca',
            5: 'fr',
            6: 'pt',
            7: 'it',
            8: 'ja',
            9: 'zh',
            10: 'pt_BR',
            11: 'ru',
            12: 'de',
          };
          return map[id] || 'en';
        };

        const selectedLanguages = res.languages.map(language => {
          return { id: mapIdToCode(language.id), name: language.label };
        });

        setHotelLanguages(selectedLanguages);
      })
      .catch(err => {
        console.warn(err);
        updateState({ isLoading: false });
        notification.error({
          message: err?.response?.data?.message,
        });
      });
  }

  function updateState(object) {
    setState(previousState => ({ ...previousState, ...object }));
  }
}
